import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fetchCards } from "../api";
import IMAGES from "../data/imageMap";
import { useSiteContent } from "../contexts/SiteContent";

export default function Breakthroughs() {
  const { t } = useSiteContent();
  const [cards, setCards] = useState([]);
  const scrollerRef = useRef(null);

  useEffect(() => {
    fetchCards("breakthroughs").then(setCards).catch(() => setCards([]));
  }, []);

  // Featured card (AlphaFold) goes in the middle; non-featured ordered around it.
  const featured = cards.find((c) => c.featured);
  const others = cards.filter((c) => !c.featured);

  return (
    <section
      id="breakthroughs"
      data-testid="breakthroughs-section"
      className="bg-white py-16 md:py-24 lg:py-28"
    >
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <div className="max-w-[720px]">
          <h2 className="text-[32px] md:text-[44px] font-semibold tracking-[-0.02em] leading-[1.1] text-neutral-900">
            {t("breakthroughs_intro.heading", "Breakthroughs")}
          </h2>
          <p className="mt-4 text-[16px] md:text-[17px] text-neutral-600 leading-relaxed">
            {t(
              "breakthroughs_intro.subtitle",
              "From folding proteins to forecasting weather, our research teams keep pushing what AI can do for science."
            )}
          </p>
        </div>
      </div>

      <div
        ref={scrollerRef}
        data-testid="breakthroughs-scroller"
        className="mt-10 flex items-stretch gap-5 overflow-x-auto snap-x snap-mandatory scrollbar-hide px-4 md:px-[max(24px,calc((100vw-1280px)/2))] pb-4"
      >
        {others.slice(0, 2).map((card) => (
          <BreakthroughCard key={card.id} card={card} />
        ))}

        {featured && (
          <Link
            key={featured.id}
            to={featured.slug ? `/breakthroughs/${featured.slug}` : "#"}
            data-testid={`breakthrough-featured-${featured.order}`}
            className="group relative shrink-0 snap-start overflow-hidden rounded-2xl w-[88vw] md:w-[700px] lg:w-[920px] h-[400px] md:h-[420px] cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_24px_56px_rgba(45,95,212,0.18)]"
          >
            <div className="absolute inset-0">
              <img
                src={IMAGES.breakthroughs[featured.order]}
                alt=""
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/60 to-white/10" />
            </div>
            <div className="relative h-full p-8 md:p-12 flex flex-col justify-center max-w-[60%]">
              <p className="text-[12px] uppercase tracking-[0.18em] font-medium text-[#2d5fd4]">
                Foundational research
              </p>
              <h3 className="mt-3 text-[32px] md:text-[44px] font-semibold tracking-[-0.02em] text-neutral-900 leading-[1.05]">
                {featured.title}
              </h3>
              <p className="mt-3 text-[15px] md:text-[16px] text-neutral-700 leading-relaxed max-w-[440px]">
                {featured.blurb}
              </p>
              <div className="mt-6">
                <span className="inline-flex items-center gap-2 rounded-full bg-neutral-900 px-5 h-10 text-[13px] font-medium text-white transition-all group-hover:bg-neutral-800">
                  Learn more
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </div>
          </Link>
        )}

        {others.slice(2).map((card) => (
          <BreakthroughCard key={card.id} card={card} />
        ))}
      </div>
    </section>
  );
}

function BreakthroughCard({ card }) {
  return (
    <Link
      to={card.slug ? `/breakthroughs/${card.slug}` : "#"}
      data-testid={`breakthrough-card-${card.order}`}
      className="group relative shrink-0 snap-start overflow-hidden rounded-2xl w-[280px] md:w-[360px] h-[400px] md:h-[420px] bg-neutral-100 border border-neutral-200 cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_18px_48px_rgba(0,0,0,0.10)]"
    >
      <div className="absolute inset-0">
        <img
          src={IMAGES.breakthroughs[card.order]}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
      </div>
      <div className="relative h-full p-6 flex flex-col justify-end text-white">
        <h3 className="text-[20px] md:text-[22px] font-semibold tracking-tight leading-snug">
          {card.title}
        </h3>
        <p className="mt-2 text-[13px] text-white/85 leading-relaxed line-clamp-3">
          {card.blurb}
        </p>
        <span className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium">
          Learn more
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  );
}
